import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { type ReactNode, useEffect, useState } from 'react'

import type { AwxConnectedAccount } from '@ankor-io/common/types/awx'

import { useUserContext } from '@/providers/user/useUserContext'

import { ConnectedAccountContext } from './ConnectedAccountContext'

export const ConnectedAccountProvider = ({ children }: { children: ReactNode }) => {
  const [connectedAccounts, setConnectedAccounts] = useState<AwxConnectedAccount[]>([])

  const [isLoading, setIsLoading] = useState(true)
  const { activeOrganizationId, activeConnectedAccountId, updateActiveConnectedAccountId } = useUserContext()
  const { getAccessToken } = useKindeAuth()

  const appendConnectedAccount = (connectedAccount: AwxConnectedAccount) => {
    setConnectedAccounts([...connectedAccounts, connectedAccount])
  }

  useEffect(() => {
    const fetchConnectedAccounts = async () => {
      setIsLoading(true)

      try {
        const res = await fetch(`/api/pay/org/${activeOrganizationId}/connected-account`, {
          headers: {
            Authorization: `Bearer ${await getAccessToken()}`,
          },
        })

        if (!res.ok) {
          throw new Error('Failed to fetch connected accounts')
        }

        const data = await res.json()
        setConnectedAccounts(data.connectedAccounts || [])

        // Set first connected account as active if none exists
        if (!activeConnectedAccountId && data.connectedAccounts?.length && data.connectedAccounts[0].id) {
          updateActiveConnectedAccountId(data.connectedAccounts[0].id)
        }
      } catch (error) {
        console.error(error)
      } finally {
        setIsLoading(false)
      }
    }
    if (activeOrganizationId) {
      fetchConnectedAccounts()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- we only want to fetch connected accounts when the active organization changes
  }, [activeOrganizationId])

  return (
    <ConnectedAccountContext.Provider
      value={{
        connectedAccounts,
        appendConnectedAccount,
        isLoading,
      }}
    >
      {children}
    </ConnectedAccountContext.Provider>
  )
}
